import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useTheme } from '../contexts/ThemeContext';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const navbarRef = useRef(null);
  const logoRef = useRef(null);
  const menuRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    try {
      const navbar = navbarRef.current;
      const logo = logoRef.current;
      const menu = menuRef.current;
      
      // Ensure refs are available before animating
      if (!navbar || !logo || !menu) {
        return;
      }
      
      // Initial animation with stagger
      const tl = gsap.timeline();
      tl.fromTo(navbar, 
        { y: -100, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out" }
      )
      // .fromTo(logo,
      //   { scale: 0, rotation: -180 },
      //   { scale: 1, rotation: 0, duration: 0.8, ease: "back.out(1.7)" },
      //   "-=0.5"
      // )
      .fromTo(menu.children,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "power2.out" },
        "-=0.3"
      );

      // Sticky animation on scroll
      const handleScroll = () => {
        try {
          const scrolled = window.scrollY > 50;
          setIsScrolled(scrolled);
          
          if (scrolled) {
            gsap.to(navbar, {
              backgroundColor: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
              duration: 0.3,
              ease: "power2.out"
            });
          } else {
            gsap.to(navbar, {
              backgroundColor: 'transparent',
              backdropFilter: 'blur(0px)',
              boxShadow: 'none',
              duration: 0.3,
              ease: "power2.out"
            });
          }
        } catch (error) {
          console.error('Error in scroll handler:', error);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => {
        try {
          window.removeEventListener('scroll', handleScroll);
        } catch (error) {
          console.error('Error removing scroll listener:', error);
        }
      };
    } catch (error) {
      console.error('Error in Navbar useEffect:', error);
    }
  }, [isDark]);

  const navItems = [
    { name: 'Home', to: '/' },
    { name: 'About', to: '/about' },
    { name: 'Services', to: '/services' },
    { name: 'Portfolio', to: '/portfolio' },
    { name: 'Contact', to: '/contact' }
  ];

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <nav 
      ref={navbarRef}
      className="fixed top-0 left-0 right-0 z-50 px-2 sm:px-4 py-2 sm:py-4 transition-all duration-300"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0 bg-gray-100/70 p-1.5 rounded-md">
            <img
              ref={logoRef}
              src="/codepactlogo.png"
              alt="Codepact Logo"
              className="w-28 sm:w-32 md:w-36 cursor-pointer hover:scale-110 transition-transform duration-300"
              onClick={closeMenu}
            />
          </div>

          {/* Desktop Navigation */}
          <div ref={menuRef} className="hidden md:block">
            <div className="ml-10 flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.to}
                  onClick={closeMenu}
                  className={`${isScrolled ? 'text-black dark:text-white' : 'text-black bg-blue-50/70 rounded-xl dark:bg-gray-800/70 dark:text-white'} ${location.pathname === item.to ? 'text-sky-700 dark:text-sky-400' : ''} hover:text-[#0284C7] dark:hover:text-primary-400 px-4 py-2 text-sm font-medium transition-all duration-300 relative group magnetic-hover`}
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-600 to-accent-600 transition-all duration-300 group-hover:w-full"></span>
                  <span className="absolute inset-0 bg-gradient-to-r from-primary-600/10 to-accent-600/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                </Link>
              ))}
            </div>
          </div>

          {/* Theme Toggle & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 hover:scale-110 hover:rotate-12"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* CTA Button */}
            <Link 
              to="/contact"
              onClick={closeMenu}
              className="btn-primary group relative overflow-hidden"
            >
              <span className="relative z-10">Get Started</span>
              <div className="absolute inset-0 bg-gradient-to-r from-accent-600 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2 sm:space-x-2">
            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-1.5 sm:p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 bg-white rounded-lg hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus:text-primary-600 transition-all duration-300 hover:scale-110 p-1"
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden animate-fade-in-down absolute left-0 right-0 top-full w-full">
            <div className="w-full px-2 pt-3 pb-4 space-y-1 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg rounded-2xl mt-2 shadow-2xl border border-gray-200/20 dark:border-gray-700/20">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.to}
                  onClick={closeMenu}
                  className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 block px-3 py-2 text-sm font-medium w-full text-left transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  {item.name}
                </Link>
              ))}
              <Link 
                to="/contact"
                onClick={closeMenu}
                className="btn-primary w-full mt-3 text-sm px-3 py-2 text-center block"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
